import { ArrowRight, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/catalyst/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Card } from '@/components/ui/v3/Card';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import {
  useGetProviderDirectoryLocationsQuery,
  useGetProviderDirectoryServiceQuery,
  useMergeProviderDirectoryLocationsMutation,
} from '@/services/api/providerDirectoryV2Service';
import type { ProviderDirectoryV2AddressSnippet } from '@/services/types/provider-directory-v2-types';

const formatAddressLine = (addr?: ProviderDirectoryV2AddressSnippet | null) => {
  if (!addr) return null;
  const parts = [
    addr.address_line1,
    addr.city,
    addr.state,
    addr.postal_code,
  ].filter(Boolean);
  return parts.length ? parts.join(', ') : (addr.formatted_address ?? null);
};

export const DedupeMergeModal = ({
  open,
  onClose,
  serviceId,
}: {
  open: boolean;
  onClose: () => void;
  serviceId: string;
}) => {
  const { data: service } = useGetProviderDirectoryServiceQuery(
    { serviceId },
    { skip: !open || !serviceId },
  );

  const [mode, setMode] = useState<'clinical' | 'routing'>('clinical');
  const [q, setQ] = useState('');
  const debouncedQ = useDebouncedValue(q.trim(), 200);

  const [canonicalId, setCanonicalId] = useState<string | null>(null);
  const [mergeIds, setMergeIds] = useState<string[]>([]);

  const [mergeLocations, { isLoading: isMerging }] =
    useMergeProviderDirectoryLocationsMutation();

  const locations = useGetProviderDirectoryLocationsQuery(
    { q: debouncedQ || undefined, limit: 50, offset: 0 },
    { skip: !open },
  );

  const defaultCanonical = useMemo(() => {
    if (!service) return null;
    if (mode === 'clinical') return service.clinical_location?.id ?? null;
    // For routing merge, default to billing if present, else records.
    return service.billing_location?.id ?? service.records_location?.id ?? null;
  }, [mode, service]);

  const effectiveCanonical = canonicalId ?? defaultCanonical;

  const canPreviewOrMerge =
    Boolean(effectiveCanonical) &&
    mergeIds.length > 0 &&
    !mergeIds.includes(effectiveCanonical ?? '');

  const handlePreview = async () => {
    if (!effectiveCanonical || !canPreviewOrMerge) return;
    try {
      const res = await mergeLocations({
        canonical_location_id: effectiveCanonical,
        merge_location_ids: mergeIds,
        dry_run: true,
      }).unwrap();
      toast.message('Preview', {
        description: `${res.affected_service_ids.length} services affected • ${res.moved_points} points moved • ${res.archived_points} duplicate points archived`,
      });
    } catch (err) {
      console.error('Preview merge failed', err);
      toast.error('Preview failed');
    }
  };

  const handleMerge = async () => {
    if (!effectiveCanonical || !canPreviewOrMerge) return;
    try {
      const res = await mergeLocations({
        canonical_location_id: effectiveCanonical,
        merge_location_ids: mergeIds,
        dry_run: false,
      }).unwrap();
      toast.success(
        `Merged ${res.merged_location_ids.length} location(s) into canonical`,
      );
      onClose();
    } catch (err) {
      console.error('Merge failed', err);
      toast.error('Merge failed');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (!next ? onClose() : null)}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Merge / dedupe</DialogTitle>
          <DialogDescription>
            Merge duplicate locations (address-identity) or routing departments
            (contact-identity).
          </DialogDescription>
        </DialogHeader>

        {!service ? (
          <div className="text-sm text-zinc-600">Loading…</div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2">
              <div className="text-xs text-zinc-500">Service</div>
              <div className="text-sm font-medium text-zinc-900">
                {service.name}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setMode('clinical')}
                className={
                  mode === 'clinical'
                    ? 'px-2.5 py-1.5 rounded-lg border text-xs font-semibold bg-blue-50 border-blue-200 text-blue-700'
                    : 'px-2.5 py-1.5 rounded-lg border text-xs font-semibold bg-white border-zinc-200 text-zinc-700 hover:bg-zinc-50'
                }
              >
                Merge locations (same address)
              </button>
              <button
                type="button"
                onClick={() => setMode('routing')}
                className={
                  mode === 'routing'
                    ? 'px-2.5 py-1.5 rounded-lg border text-xs font-semibold bg-blue-50 border-blue-200 text-blue-700'
                    : 'px-2.5 py-1.5 rounded-lg border text-xs font-semibold bg-white border-zinc-200 text-zinc-700 hover:bg-zinc-50'
                }
              >
                Merge routing departments (same contacts)
              </button>
            </div>

            <Card className="p-4 border border-zinc-200/70">
              <div className="text-sm font-semibold text-zinc-900">
                Choose canonical + duplicates
              </div>
              <div className="text-xs text-zinc-500 mt-0.5">
                Canonical stays active; selected duplicates are archived and
                relinked.
              </div>

              <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-3">
                <div className="rounded-xl border border-zinc-200 p-3">
                  <div className="text-xs font-semibold text-zinc-600">
                    Canonical
                  </div>
                  <div className="text-sm font-medium text-zinc-900 mt-1">
                    {effectiveCanonical ?? '—'}
                  </div>
                  <div className="text-xs text-zinc-500 mt-1">
                    {mode === 'clinical'
                      ? 'Defaults to clinical location'
                      : 'Defaults to billing, then records'}
                  </div>
                </div>
                <div className="rounded-xl border border-zinc-200 p-3">
                  <div className="text-xs font-semibold text-zinc-600">
                    Merge these
                  </div>
                  <div className="text-sm font-medium text-zinc-900 mt-1">
                    {mergeIds.length ? mergeIds.join(', ') : '—'}
                  </div>
                  <div className="text-xs text-zinc-500 mt-1">
                    {mergeIds.length} selected
                  </div>
                </div>
              </div>

              <div className="mt-3 flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="h-4 w-4 text-zinc-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                  <Input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search locations/departments…"
                    className="pl-8"
                  />
                </div>
                <Button
                  variant="outline"
                  onClick={() => {
                    setCanonicalId(null);
                    setMergeIds([]);
                  }}
                >
                  Reset
                </Button>
              </div>

              <div className="mt-3 max-h-[320px] overflow-auto space-y-2">
                {(locations.data?.results ?? []).map((l) => {
                  const isCanonical = effectiveCanonical === l.id;
                  const isSelected = mergeIds.includes(l.id);
                  return (
                    <div
                      key={l.id}
                      className="rounded-lg border border-zinc-200 bg-white p-3 flex items-start justify-between gap-3"
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-zinc-900 truncate">
                          {l.name}
                        </div>
                        <div className="text-xs text-zinc-500 truncate mt-0.5">
                          {formatAddressLine(l.address ?? null) ?? '—'}
                        </div>
                        <div className="text-[11px] text-zinc-500 mt-0.5 font-mono">
                          {l.id}
                        </div>
                      </div>

                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={isCanonical}
                          onClick={() => setCanonicalId(l.id)}
                        >
                          Keep
                        </Button>
                        <Button
                          variant={isSelected ? 'default' : 'outline'}
                          size="sm"
                          disabled={isCanonical}
                          onClick={() =>
                            setMergeIds((prev) =>
                              prev.includes(l.id)
                                ? prev.filter((x) => x !== l.id)
                                : [...prev, l.id],
                            )
                          }
                        >
                          {isSelected ? 'Selected' : 'Select'}
                        </Button>
                      </div>
                    </div>
                  );
                })}

                {!locations.isFetching &&
                (locations.data?.results ?? []).length === 0 ? (
                  <div className="text-sm text-zinc-600 py-3">No results.</div>
                ) : null}
              </div>
            </Card>

            <div className="flex items-center justify-between gap-3 pt-1">
              <div className="text-xs text-zinc-500">
                Use Preview first to confirm service impact.
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  variant="outline"
                  onClick={handlePreview}
                  disabled={!canPreviewOrMerge || isMerging}
                >
                  Preview
                </Button>
                <Button
                  onClick={handleMerge}
                  disabled={!canPreviewOrMerge || isMerging}
                >
                  <ArrowRight className="h-4 w-4 mr-1" />
                  Merge
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
